/** Claim fornitori (elenco e scheda) e scheda fornitore. */
import type { Forma } from "../forma";

export const it = {
  elenco: {
    titolo: "Claim fornitori",
    sottotitolo: "Il file dell'amministrazione dentro il portale: claim ai fornitori, recupero, blocco dei pagamenti.",
    nuovo: "Nuovo claim",
    cerca: "Cerca fornitore, brand, problema…",
    tuttiStati: "Tutti gli stati",
    soloAperti: "Solo aperti",
    kpiAperti: "Claim aperti",
    kpiUsd: "Valore in USD",
    kpiEur: "Valore in EUR",
    kpiCollegati: "Collegati a un red flag",
    kpiProposti: "+{{n}} proposti",
    kpiBloccati: "Pagamenti bloccati",
    claim: "Claim",
    fornitore: "Fornitore",
    brand: "Brand",
    problema: "Problema",
    risoluzione: "Risoluzione",
    usd: "USD",
    eur: "EUR",
    redflag: "Red flag",
    stato: "Stato",
    blocco: "Blocco pagamento",
    nota: "Stati del claim: Aperto → Notificato al fornitore → In trattativa → Accettato / Contestato → Recuperato.",
    vuoto: "Nessun claim con questi filtri",
  },
  nuovo: {
    titolo: "Nuovo claim",
    fornitore: "Fornitore (VendorHub)",
    fornitoreTesto: "Fornitore come scritto",
    brand: "Brand",
    problema: "Problema",
    risoluzione: "Risoluzione",
    importo: "Importo",
    valuta: "Valuta",
    creato: "Claim {{codice}} creato",
  },
  scheda: {
    cambiaStato: "Cambia stato",
    statoOriginale: "Nel riepilogo: «{{s}}»",
    dati: "Claim",
    fornitore: "Fornitore",
    brand: "Brand",
    problema: "Problema",
    risoluzione: "Risoluzione",
    valore: "Valore stimato",
    valuta: "Valuta",
    modalita: "Modalità di recupero",
    daDecidere: "da decidere",
    recuperato: "Recuperato",
    blocco: "Blocco pagamento",
    bloccoSi: "Sì, fatture bloccate",
    note: "Note",
    salvato: "Claim salvato",
    storico: "Storico",
    nessunoStorico: "Nessun movimento",
    rfTitolo: "Red flag collegato",
    rfProposto: "Collegamento proposto ({{codice}}){{verifica}}: lo approva l'admin in «Proposte».",
    rfDaVerificare: ", da verificare",
    nessunRf: "Nessun red flag",
    nessunRfNota: "Claim nato fuori dai red flag.",
    proponiAltro: "Proponi un altro collegamento",
    proponi: "Proponi collegamento",
    proponiTitolo: "Collega il claim a un red flag",
    segnalazione: "Segnalazione",
    segnalazioneCerca: "codice o cliente…",
    daVerificare: "Da verificare",
    documenti: "Documenti",
    documentiInArrivo: "Nota di debito, mail al fornitore, accordo: in arrivo",
  },
  fornitore: {
    elencoTitolo: "Fornitori",
    elencoSottotitolo: "Anagrafica letta dal VendorHub, in sola lettura. Cerca per nome o codice.",
    cerca: "Nome o codice del fornitore…",
    nome: "Fornitore",
    codice: "Codice",
    paese: "Paese",
    vuoto: "Nessun fornitore trovato",
    sottotitolo: "Scheda del fornitore: anagrafica dal VendorHub, NC da audit da Audit Plan, casi da Red Flag.",
    anagraficaRo: "anagrafica in sola lettura",
    rating: "Rating VendorHub",
    ratingNota: "letto dal VendorHub",
    redflag: "Red flag",
    ncRf: "NC da Red Flag",
    ncAudit: "NC da audit",
    ncAuditNota: "da Audit Plan",
    claim: "Claim",
    rfNc: "Red flag e NC",
    anagrafica: "Anagrafica",
    nomeLocale: "Nome locale",
    partitaIva: "Partita IVA",
    indirizzo: "Indirizzo",
    statoVh: "Stato nel VendorHub",
    stabilimenti: "Stabilimenti",
  },
};

export const en: Forma<typeof it> = {
  elenco: {
    titolo: "Supplier claims",
    sottotitolo: "The accounting file inside the portal: supplier claims, recovery, payment holds.",
    nuovo: "New claim",
    cerca: "Search supplier, brand, issue…",
    tuttiStati: "All statuses",
    soloAperti: "Open only",
    kpiAperti: "Open claims",
    kpiUsd: "Value in USD",
    kpiEur: "Value in EUR",
    kpiCollegati: "Linked to a red flag",
    kpiProposti: "+{{n}} proposed",
    kpiBloccati: "Payments on hold",
    claim: "Claim",
    fornitore: "Supplier",
    brand: "Brand",
    problema: "Issue",
    risoluzione: "Resolution",
    usd: "USD",
    eur: "EUR",
    redflag: "Red flag",
    stato: "Status",
    blocco: "Payment hold",
    nota: "Claim statuses: Open → Notified to supplier → Negotiating → Accepted / Disputed → Recovered.",
    vuoto: "No claims match these filters",
  },
  nuovo: {
    titolo: "New claim",
    fornitore: "Supplier (VendorHub)",
    fornitoreTesto: "Supplier as written",
    brand: "Brand",
    problema: "Issue",
    risoluzione: "Resolution",
    importo: "Amount",
    valuta: "Currency",
    creato: "Claim {{codice}} created",
  },
  scheda: {
    cambiaStato: "Change status",
    statoOriginale: "In the summary: “{{s}}”",
    dati: "Claim",
    fornitore: "Supplier",
    brand: "Brand",
    problema: "Issue",
    risoluzione: "Resolution",
    valore: "Estimated value",
    valuta: "Currency",
    modalita: "Recovery method",
    daDecidere: "to be decided",
    recuperato: "Recovered",
    blocco: "Payment hold",
    bloccoSi: "Yes, invoices on hold",
    note: "Notes",
    salvato: "Claim saved",
    storico: "History",
    nessunoStorico: "No activity",
    rfTitolo: "Linked red flag",
    rfProposto: "Link proposed ({{codice}}){{verifica}}: the admin approves it in “Proposals”.",
    rfDaVerificare: ", to be checked",
    nessunRf: "No red flag",
    nessunRfNota: "Claim raised outside the red flags.",
    proponiAltro: "Propose another link",
    proponi: "Propose a link",
    proponiTitolo: "Link the claim to a red flag",
    segnalazione: "Report",
    segnalazioneCerca: "code or customer…",
    daVerificare: "To be checked",
    documenti: "Documents",
    documentiInArrivo: "Debit note, email to the supplier, agreement: coming soon",
  },
  fornitore: {
    elencoTitolo: "Suppliers",
    elencoSottotitolo: "Master data read from VendorHub, read only. Search by name or code.",
    cerca: "Supplier name or code…",
    nome: "Supplier",
    codice: "Code",
    paese: "Country",
    vuoto: "No supplier found",
    sottotitolo: "Supplier sheet: master data from VendorHub, audit NCs from Audit Plan, cases from Red Flags.",
    anagraficaRo: "read-only master data",
    rating: "VendorHub rating",
    ratingNota: "read from VendorHub",
    redflag: "Red flags",
    ncRf: "Red flag NCs",
    ncAudit: "Audit NCs",
    ncAuditNota: "from Audit Plan",
    claim: "Claims",
    rfNc: "Red flags and NCs",
    anagrafica: "Master data",
    nomeLocale: "Local name",
    partitaIva: "VAT number",
    indirizzo: "Address",
    statoVh: "VendorHub status",
    stabilimenti: "Plants",
  },
};
